// ══════════════════════════════════════════════════════
//  validateTokenUnlock — Surf unlock 数据 sanity check
//
//  背景：Surf /token/tokenomics?symbol=UNI 不做项目消歧，
//        会把任意叫 UNI 的新代币的数据返回，污染 Uniswap 分析。
//  策略：拿到 Surf 数据后跟 CoinGecko total_supply / market_cap
//        交叉验证，明显不匹配就丢弃。
// ══════════════════════════════════════════════════════

/**
 * @param {object|null} tokenUnlock - Surf 返回的 unlock 数据（含 events / allocationTotals / next30dAmount）
 * @param {object|null} cgData - CoinGecko 数据（含 market_data.total_supply / circulating_supply / market_cap.usd）
 * @returns {object|null} - 数据合理返回原对象；明显不匹配返回 null（让下游显示"暂无解锁数据"）
 */
export function validateTokenUnlock(tokenUnlock, cgData) {
  if (!tokenUnlock?.ok) return tokenUnlock;

  const md = cgData?.market_data || {};
  const cgTotalSupply = num(md.total_supply);
  const cgCirculating = num(md.circulating_supply);
  const cgMarketCap = num(md.market_cap?.usd);
  const cgSupply = cgTotalSupply || cgCirculating;

  // CoinGecko 没数据时无法验证，保守保留原数据
  if (!cgSupply) return tokenUnlock;

  // ⚠ 注意：proxy 已经把 Surf 的 cumulative → delta，所以这里的
  //   allocationTotals 现在是「最后一个事件的累计状态」，约等于 total_supply。
  //   每个 event.totalAmount 也是真实单次释放量。阈值因此可以收紧到
  //   物理常识层级（任何 > total_supply 都是数据错）。

  // Check 1: 全周期累计分配总量 > 1.2x CG total_supply → 数据明显错
  //   1.2x 给一点 buffer，应对 CG 没把 staking emission 算进 total_supply 的情况
  const totalAllocAmt = (tokenUnlock.allocationTotals || [])
    .reduce((s, a) => s + (num(a?.amount) || 0), 0);
  if (totalAllocAmt > cgSupply * 1.2) {
    console.warn(`[unlock-validate] 累计分配 ${humanize(totalAllocAmt)} > 1.2x CG total_supply ${humanize(cgSupply)}（${(totalAllocAmt/cgSupply).toFixed(2)}x），数据异常，丢弃`);
    return null;
  }

  // Check 2: 30d 单次释放量 > CG total_supply = 物理上不可能
  const next30dAmount = num(tokenUnlock.next30dAmount) || 0;
  if (next30dAmount > cgSupply) {
    console.warn(`[unlock-validate] 30d 释放量 ${humanize(next30dAmount)} > total_supply ${humanize(cgSupply)}，丢弃`);
    return null;
  }

  // Check 3: 任意单个 event 释放量 > 50% total_supply = 极端情况，要么是 TGE 当天
  //   要么是数据错。早期 TGE-post 项目可能在 cliff 处释放 ~30%，但 >50% 已是异常。
  const events = Array.isArray(tokenUnlock.events) ? tokenUnlock.events : [];
  const maxEventAmt = events.reduce((m, e) => Math.max(m, num(e?.totalAmount) || 0), 0);
  if (maxEventAmt > cgSupply * 0.5) {
    console.warn(`[unlock-validate] 单事件释放 ${humanize(maxEventAmt)} > 50% supply ${humanize(cgSupply)}，丢弃`);
    return null;
  }

  return tokenUnlock;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function humanize(v) {
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
}
